import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error,
            errorInfo: null
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('🚨 React Error Boundary caught an error:', error);
        console.error('🚨 Error Info:', errorInfo);

        this.setState({
            error,
            errorInfo
        });
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    public render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        p: 4,
                        backgroundColor: '#f5f5f5'
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            maxWidth: 600,
                            textAlign: 'center'
                        }}
                    >
                        <BugReportIcon
                            sx={{
                                fontSize: 64,
                                color: 'error.main',
                                mb: 2
                            }}
                        />

                        <Typography variant="h4" gutterBottom color="error">
                            Something went wrong
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
                            The application encountered an unexpected error. This has been logged for debugging.
                        </Typography>

                        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Error Details:
                            </Typography>
                            <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem' }}>
                                {this.state.error?.message}
                            </Typography>
                        </Alert>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleReload}
                                color="primary"
                            >
                                Reload Application
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={this.handleReset}
                                color="secondary"
                            >
                                Try Again
                            </Button>
                        </Box>

                        {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                            <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Component Stack (Development):
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                        fontSize: '0.7rem',
                                        maxHeight: 200,
                                        overflow: 'auto'
                                    }}
                                >
                                    {this.state.errorInfo.componentStack}
                                </Typography>
                            </Alert>
                        )}
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
